import { HOUSE_PALETTES, MULTI, PLAYER_H, VIEW_H, WARM } from "./constants";
import type { House, Obstacle, Player } from "./types";
import { obstacleWorldX } from "./world";

export function resizeCanvas(canvas: HTMLCanvasElement): { w: number; h: number; scale: number } {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const cssW = canvas.clientWidth;
  const cssH = canvas.clientHeight;
  canvas.width = Math.max(1, Math.floor(cssW * dpr));
  canvas.height = Math.max(1, Math.floor(cssH * dpr));
  const scale = canvas.height / VIEW_H;
  return { w: canvas.width / scale, h: VIEW_H, scale };
}

export function drawSky(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  progress: number,
  time: number,
): void {
  const glow = Math.min(1, progress / 22);
  const top = mix("#070b14", "#141c32", glow * 0.45);
  const mid = mix("#10182a", "#2a2438", glow * 0.55);
  const hor = mix("#1b2236", "#5a3a28", glow * 0.7);
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, top);
  g.addColorStop(0.55, mid);
  g.addColorStop(1, hor);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  ctx.fillStyle = "rgba(255,236,190,0.92)";
  ctx.beginPath();
  ctx.arc(w * 0.78, h * 0.16, 28, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(255,236,190,0.12)";
  ctx.beginPath();
  ctx.arc(w * 0.78, h * 0.16, 52, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "rgba(255,255,255,0.85)";
  for (let i = 0; i < 48; i++) {
    const sx = ((i * 97) % 2000) / 2000 * w;
    const sy = ((i * 53) % 900) / 900 * h * 0.48;
    const tw = 0.45 + Math.sin(time * 2 + i) * 0.25;
    ctx.globalAlpha = tw;
    ctx.fillRect(sx, sy, 1.4, 1.4);
  }
  ctx.globalAlpha = 1;
}

export function drawHills(
  ctx: CanvasRenderingContext2D,
  cameraX: number,
  w: number,
  h: number,
  progress: number,
): void {
  const glow = Math.min(1, progress / 20);
  ctx.fillStyle = mix("#121820", "#1c2430", glow * 0.3);
  drawRidge(ctx, cameraX * 0.15, w, h * 0.62, 70, 0.0011);
  ctx.fillStyle = mix("#0e141c", "#182028", glow * 0.25);
  drawRidge(ctx, cameraX * 0.28, w, h * 0.7, 54, 0.0016);
}

function drawRidge(
  ctx: CanvasRenderingContext2D,
  shift: number,
  w: number,
  base: number,
  amp: number,
  freq: number,
): void {
  ctx.beginPath();
  ctx.moveTo(0, VIEW_H);
  for (let x = 0; x <= w; x += 8) {
    const y = base + Math.sin((x + shift) * freq * 40) * amp + Math.sin((x + shift) * freq * 17) * amp * 0.45;
    ctx.lineTo(x, y);
  }
  ctx.lineTo(w, VIEW_H);
  ctx.closePath();
  ctx.fill();
}

export function drawFarHouses(
  ctx: CanvasRenderingContext2D,
  cameraX: number,
  w: number,
  progress: number,
): void {
  const litN = Math.min(14, 3 + Math.floor(progress * 0.7));
  const start = Math.floor(cameraX * 0.22 / 90);
  for (let i = start - 1; i < start + 22; i++) {
    const x = i * 90 - cameraX * 0.22;
    if (x < -80 || x > w + 40) continue;
    const hgt = 70 + ((i * 17) % 50);
    const y = 560 - hgt;
    ctx.fillStyle = "#161b24";
    ctx.fillRect(x, y, 54, hgt);
    const windows = 2 + (i % 3);
    for (let wy = 0; wy < windows; wy++) {
      const on = (i + wy) % 7 < litN / 3 || progress > (i % 12);
      ctx.fillStyle = on ? "rgba(255,210,130,0.55)" : "rgba(20,24,32,0.8)";
      ctx.fillRect(x + 8, y + 10 + wy * 16, 8, 8);
      ctx.fillRect(x + 28, y + 10 + wy * 16, 8, 8);
    }
    if (progress > 4) {
      ctx.strokeStyle = "rgba(255,210,130,0.25)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + 54, y);
      ctx.stroke();
    }
  }
}

export function drawGround(
  ctx: CanvasRenderingContext2D,
  cameraX: number,
  w: number,
  h: number,
  progress: number,
): void {
  const glow = Math.min(1, progress / 18);
  const street = ctx.createLinearGradient(0, 560, 0, h);
  street.addColorStop(0, mix("#1a221c", "#2a3228", glow * 0.4));
  street.addColorStop(1, mix("#121814", "#1c2218", glow * 0.3));
  ctx.fillStyle = street;
  ctx.fillRect(0, 560, w, h - 560);

  ctx.fillStyle = "rgba(230,236,240,0.18)";
  ctx.fillRect(0, 556, w, 8);

  ctx.fillStyle = "rgba(255,255,255,0.08)";
  for (let i = Math.floor(cameraX / 140) - 1; i < cameraX / 140 + 14; i++) {
    const x = i * 140 - cameraX;
    ctx.fillRect(x, 628, 46, 5);
  }

  if (glow > 0.15) {
    ctx.fillStyle = `rgba(255, 196, 110, ${0.04 + glow * 0.08})`;
    ctx.fillRect(0, 500, w, 80);
  }
}

export function drawHouse(
  ctx: CanvasRenderingContext2D,
  h: House,
  now: number,
): void {
  const pal = HOUSE_PALETTES[h.palette] ?? HOUSE_PALETTES[0];
  const x = h.x;
  const roofY = h.roofY;
  const bodyTop = roofY + 10;
  const ground = 568;
  const bodyH = ground - bodyTop;
  const rng = mulberry32Local(h.seed);

  ctx.fillStyle = "rgba(0,0,0,0.28)";
  ctx.beginPath();
  ctx.ellipse(x + h.w * 0.5, ground + 6, h.w * 0.48, 10, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = pal.wall;
  ctx.fillRect(x + 4, bodyTop, h.w - 8, bodyH);

  if (pal.brick) {
    ctx.fillStyle = "rgba(0,0,0,0.12)";
    for (let by = bodyTop + 8; by < ground; by += 12) {
      for (let bx = x + 8 + ((by / 12) % 2) * 8; bx < x + h.w - 12; bx += 16) {
        ctx.fillRect(bx, by, 14, 10);
      }
    }
  }

  ctx.fillStyle = pal.trim;
  ctx.fillRect(x + 4, bodyTop, h.w - 8, 6);
  ctx.fillRect(x + 4, ground - 8, h.w - 8, 8);

  drawRoof(ctx, h, pal.roof);

  const winLit = 0.15 + h.lightT * 0.85;
  const cols = h.stories === 2 ? 3 : 2;
  const rows = h.stories;
  const winW = 16;
  const winH = 20;
  const margin = 22;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const wx = x + margin + c * ((h.w - margin * 2 - winW) / Math.max(1, cols - 1));
      const wy = bodyTop + 22 + r * (h.stories === 2 ? 48 : 36);
      ctx.fillStyle = pal.trim;
      ctx.fillRect(wx - 2, wy - 2, winW + 4, winH + 4);
      ctx.fillStyle = `rgba(255, 214, 140, ${winLit * (0.55 + rng() * 0.45)})`;
      ctx.fillRect(wx, wy, winW, winH);
      ctx.strokeStyle = pal.trim;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(wx + winW / 2, wy);
      ctx.lineTo(wx + winW / 2, wy + winH);
      ctx.moveTo(wx, wy + winH / 2);
      ctx.lineTo(wx + winW, wy + winH / 2);
      ctx.stroke();
    }
  }

  const doorW = 22;
  const doorH = 38;
  const dx = x + h.w * 0.5 - doorW / 2;
  const dy = ground - doorH;
  ctx.fillStyle = pal.trim;
  ctx.fillRect(dx - 3, dy - 3, doorW + 6, doorH + 3);
  ctx.fillStyle = "#2a1c14";
  ctx.fillRect(dx, dy, doorW, doorH);
  if (h.hasWreath) {
    ctx.strokeStyle = "#2f5a38";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(dx + doorW / 2, dy + 12, 7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = "#b83a3a";
    ctx.beginPath();
    ctx.arc(dx + doorW / 2, dy + 20, 2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = h.lightT > 0.4 ? "#e8c36a" : "#6a5840";
  ctx.beginPath();
  ctx.arc(dx + doorW - 5, dy + doorH * 0.5, 1.6, 0, Math.PI * 2);
  ctx.fill();

  if (h.hasColumns) {
    const colY0 = bodyTop + 8;
    const colH = ground - colY0 - 4;
    drawColumn(ctx, x + 16, colY0, colH, h);
    drawColumn(ctx, x + h.w - 22, colY0, colH, h);
  }

  if (h.hasTree) {
    drawYardTree(ctx, x - 18, ground, h);
  }

  drawHouseLights(ctx, h, now);

  for (const o of h.obstacles) {
    drawObstacle(ctx, h, o, now);
  }

  drawLadder(ctx, h);
}

function drawRoof(ctx: CanvasRenderingContext2D, h: House, color: string): void {
  const x = h.x;
  const y = h.roofY;
  const peak = y - h.roofH;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x - 8, y + 8);
  ctx.lineTo(x + h.w * 0.5, peak);
  ctx.lineTo(x + h.w + 8, y + 8);
  ctx.lineTo(x + h.w + 4, y + 16);
  ctx.lineTo(x - 4, y + 16);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x - 4, y + 6);
  ctx.lineTo(x + h.w * 0.5, peak + 4);
  ctx.lineTo(x + h.w + 4, y + 6);
  ctx.stroke();
}

function drawColumn(ctx: CanvasRenderingContext2D, x: number, y: number, hgt: number, house: House): void {
  ctx.fillStyle = "#d8d0c4";
  ctx.fillRect(x, y, 7, hgt);
  ctx.fillStyle = "#eee8dc";
  ctx.fillRect(x + 1, y, 2, hgt);
  if (house.lightT > 0) {
    const colors = house.lightStyle === "warm" ? WARM : MULTI;
    const n = 7;
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      if (t > house.lightT) continue;
      ctx.fillStyle = colors[i % colors.length] ?? "#ffd27a";
      ctx.beginPath();
      ctx.arc(x + 3.5, y + 8 + t * (hgt - 16), 2.1, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawYardTree(ctx: CanvasRenderingContext2D, x: number, ground: number, house: House): void {
  ctx.fillStyle = "#3a2a1c";
  ctx.fillRect(x + 10, ground - 18, 6, 18);
  ctx.fillStyle = "#1f3d28";
  ctx.beginPath();
  ctx.moveTo(x + 13, ground - 78);
  ctx.lineTo(x + 36, ground - 18);
  ctx.lineTo(x - 10, ground - 18);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#2a5340";
  ctx.beginPath();
  ctx.moveTo(x + 13, ground - 70);
  ctx.lineTo(x + 30, ground - 28);
  ctx.lineTo(x - 4, ground - 28);
  ctx.closePath();
  ctx.fill();
  if (house.lightT > 0.2) {
    const colors = house.lightStyle === "warm" ? WARM : MULTI;
    const spots = [
      [x + 8, ground - 40],
      [x + 20, ground - 48],
      [x + 12, ground - 60],
      [x + 18, ground - 34],
      [x + 6, ground - 28],
    ];
    spots.forEach(([lx, ly], i) => {
      if (i / spots.length > house.lightT) return;
      ctx.fillStyle = colors[i % colors.length] ?? "#ffd27a";
      ctx.beginPath();
      ctx.arc(lx, ly, 2.2, 0, Math.PI * 2);
      ctx.fill();
    });
  }
}

function drawHouseLights(ctx: CanvasRenderingContext2D, h: House, now: number): void {
  if (h.lightT <= 0) return;
  const colors = h.lightStyle === "warm" ? WARM : MULTI;
  const peakX = h.x + h.w * 0.5;
  const peakY = h.roofY - h.roofH;
  const left = { x: h.x - 6, y: h.roofY + 6 };
  const right = { x: h.x + h.w + 6, y: h.roofY + 6 };
  const n = 22;
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    if (t > h.lightT) continue;
    const p = t < 0.5
      ? lerp2(left, { x: peakX, y: peakY }, t * 2)
      : lerp2({ x: peakX, y: peakY }, right, (t - 0.5) * 2);
    const col = colors[i % colors.length] ?? "#ffd27a";
    const pulse = 0.75 + Math.sin(now * 6 + i) * 0.25;
    ctx.save();
    ctx.fillStyle = col;
    ctx.shadowColor = col;
    ctx.shadowBlur = 8 * pulse;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 2.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  if (h.lightT > 0.55) {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.fillStyle = h.lightStyle === "warm"
      ? `rgba(255, 190, 90, ${0.08 + h.lightT * 0.1})`
      : `rgba(180, 200, 255, ${0.06 + h.lightT * 0.08})`;
    ctx.beginPath();
    ctx.ellipse(h.x + h.w / 2, h.roofY + 40, h.w * 0.55, 70, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawObstacle(
  ctx: CanvasRenderingContext2D,
  h: House,
  o: Obstacle,
  now: number,
): void {
  const ox = obstacleWorldX(h, o, now);
  const y = h.roofY;
  if (o.kind === "chimney") {
    ctx.fillStyle = "#6a3a32";
    ctx.fillRect(ox, y - 36, o.w, 40);
    ctx.fillStyle = "#4a2824";
    ctx.fillRect(ox - 3, y - 40, o.w + 6, 8);
    ctx.fillStyle = "rgba(180,180,180,0.35)";
    ctx.beginPath();
    ctx.ellipse(ox + o.w / 2, y - 48 - Math.sin(now + o.phase) * 4, 6, 8, 0, 0, Math.PI * 2);
    ctx.fill();
  } else if (o.kind === "ice") {
    ctx.fillStyle = "rgba(190, 230, 255, 0.45)";
    ctx.beginPath();
    ctx.ellipse(ox + o.w / 2, y + 4, o.w / 2, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(230, 250, 255, 0.7)";
    ctx.stroke();
  } else if (o.kind === "squirrel") {
    ctx.fillStyle = "#6a4028";
    ctx.beginPath();
    ctx.ellipse(ox, y - 6, 8, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(ox + 7, y - 9, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#6a4028";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(ox - 6, y - 6);
    ctx.quadraticCurveTo(ox - 16, y - 22, ox - 4, y - 14);
    ctx.stroke();
  } else {
    ctx.fillStyle = "#f2f6f8";
    ctx.beginPath();
    ctx.ellipse(ox + o.w * 0.45, y - 8, o.w * 0.42, 18, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(ox + o.w * 0.45, y - 30, 16, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#1a1a1a";
    ctx.beginPath();
    ctx.arc(ox + o.w * 0.38, y - 33, 2, 0, Math.PI * 2);
    ctx.arc(ox + o.w * 0.52, y - 33, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#c45c2a";
    ctx.fillRect(ox + o.w * 0.2, y - 18, o.w * 0.5, 5);
    ctx.fillStyle = "#3a7a4a";
    ctx.fillRect(ox + 4, y + 2, 4, 10);
  }
}

function drawLadder(ctx: CanvasRenderingContext2D, h: House): void {
  const x = h.x + h.w - 18;
  const top = h.roofY + 14;
  const bot = 568;
  ctx.strokeStyle = "#8a6a40";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(x, top);
  ctx.lineTo(x, bot);
  ctx.moveTo(x + 12, top);
  ctx.lineTo(x + 12, bot);
  ctx.stroke();
  ctx.lineWidth = 2;
  for (let y = top + 10; y < bot; y += 14) {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 12, y);
    ctx.stroke();
  }
}

export function drawPlayer(
  ctx: CanvasRenderingContext2D,
  p: Player,
  falling: boolean,
): void {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rotation);
  const bob = p.onRoof ? Math.sin(p.runT * 16) * 2 : 0;
  ctx.translate(0, bob);

  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.ellipse(0, PLAYER_H * 0.5 + 4, 14, 4, 0, 0, Math.PI * 2);
  ctx.fill();

  const legSwing = p.onRoof ? Math.sin(p.runT * 16) * 10 : falling ? 18 : 4;
  ctx.strokeStyle = "#3a2618";
  ctx.lineWidth = 5;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(-4, 10);
  ctx.lineTo(-6 - legSwing * 0.15, 24);
  ctx.moveTo(5, 10);
  ctx.lineTo(7 + legSwing * 0.15, 24);
  ctx.stroke();
  ctx.fillStyle = "#2a1c12";
  ctx.fillRect(-10, 22, 8, 5);
  ctx.fillRect(4, 22, 8, 5);

  ctx.fillStyle = "#5a3a24";
  ctx.beginPath();
  ctx.ellipse(0, -2, 15, 18, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "#d4551a";
  ctx.beginPath();
  ctx.moveTo(-13, -8);
  ctx.lineTo(13, -8);
  ctx.lineTo(15, 10);
  ctx.lineTo(-15, 10);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#f0e6c8";
  ctx.fillRect(-12, -2, 24, 3);
  ctx.fillRect(-12, 6, 24, 2);
  ctx.fillStyle = "#1a2a22";
  ctx.font = "bold 6px ui-sans-serif, system-ui";
  ctx.textAlign = "center";
  ctx.fillText("SLC", 0, 5);

  const arm = p.onRoof ? Math.sin(p.runT * 16 + Math.PI) * 12 : falling ? 28 : -16;
  ctx.strokeStyle = "#5a3a24";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(-12, -4);
  ctx.lineTo(-18, 6 + arm * 0.15);
  ctx.moveTo(12, -4);
  ctx.lineTo(16, 4 - arm * 0.1);
  ctx.stroke();

  ctx.fillStyle = "#6a442c";
  ctx.beginPath();
  ctx.arc(0, -22, 11, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#3a2818";
  ctx.beginPath();
  ctx.ellipse(-8, -28, 4, 5, -0.4, 0, Math.PI * 2);
  ctx.ellipse(8, -28, 4, 5, 0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#1a140e";
  ctx.beginPath();
  ctx.arc(-4, -22, 1.6, 0, Math.PI * 2);
  ctx.arc(4, -22, 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#1a140e";
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.arc(0, -18, 3.2, 0.15, Math.PI - 0.15);
  ctx.stroke();

  ctx.restore();
}

export function drawHudGlow(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  progress: number,
): void {
  const glow = Math.min(0.35, progress * 0.018);
  const g = ctx.createRadialGradient(w * 0.5, h * 0.75, 40, w * 0.5, h * 0.7, w * 0.7);
  g.addColorStop(0, `rgba(255, 180, 80, ${glow})`);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}

function lerp2(a: { x: number; y: number }, b: { x: number; y: number }, t: number) {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function mix(a: string, b: string, t: number): string {
  const ca = hex(a);
  const cb = hex(b);
  const m = (i: number) => Math.round(ca[i]! + (cb[i]! - ca[i]!) * t);
  return `rgb(${m(0)},${m(1)},${m(2)})`;
}

function hex(c: string): [number, number, number] {
  const n = c.replace("#", "");
  return [
    parseInt(n.slice(0, 2), 16),
    parseInt(n.slice(2, 4), 16),
    parseInt(n.slice(4, 6), 16),
  ];
}

function mulberry32Local(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
